import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../../../../axios';
import { AppThunk } from '../../../../../store';
import { AddCommentDto, CommentDto, DeleteCommentDto, UpdateCommentDto } from './AnnotationComments.types';

type State = {
  isLoadingComments: boolean;
  comments: CommentDto[];
  commentsCount: number;
  addedSuccessfully: boolean;
  addedFailed: boolean;
};

const initialState: State = {
  isLoadingComments: false,
  comments: [],
  commentsCount: 0,
  addedSuccessfully: false,
  addedFailed: false
};

const annotationCommentsSlice = createSlice({
  name: 'annotationForm',
  initialState,
  reducers: {
    setComments(state, action: PayloadAction<CommentDto[]>) {
      state.comments = action.payload;
    },
    setCommentsCount(state, action: PayloadAction<number>) {
      state.commentsCount = action.payload;
    },
    startLoadingComments(state) {
      state.isLoadingComments = true;
    },
    finishLoadingComments(state) {
      state.isLoadingComments = false;
    },
    setAddedResponseFlags(state, action: PayloadAction<{ addedSuccessfully: boolean; addedFailed: boolean }>) {
      const { addedSuccessfully, addedFailed } = action.payload;
      state.addedSuccessfully = addedSuccessfully;
      state.addedFailed = addedFailed;
    }
  }
});

export const { setComments, setCommentsCount, startLoadingComments, finishLoadingComments, setAddedResponseFlags } = annotationCommentsSlice.actions;

export default annotationCommentsSlice.reducer;

export const getAnnotationCommentsCount =
  (showSpinner = true): AppThunk =>
  async (dispatch, getState) => {
    const { selectedAnnotationForEdit } = getState().annotationForm;

    if (!selectedAnnotationForEdit) {
      return;
    }

    if (showSpinner) {
      dispatch(startLoadingComments());
    }

    const response = await axios.get<number>(`comments/${selectedAnnotationForEdit.id}/count`);
    const commentsCount = response.data;
    dispatch(setCommentsCount(commentsCount));

    dispatch(finishLoadingComments());
  };

export const getAnnotationComments =
  (showSpinner = true): AppThunk =>
  async (dispatch, getState) => {
    const { selectedAnnotationForEdit } = getState().annotationForm;

    if (!selectedAnnotationForEdit) {
      return;
    }

    if (showSpinner) {
      dispatch(startLoadingComments());
    }

    const response = await axios.get<CommentDto[]>(`comments/${selectedAnnotationForEdit.id}`);
    const comments = response.data;
    dispatch(setComments(comments));

    dispatch(finishLoadingComments());
  };

export const addComment =
  (parentIds: string[], message: string): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setAddedResponseFlags({ addedFailed: false, addedSuccessfully: false }));

    const commentsCount = getState().annotationComments.commentsCount;
    const itemId = getState().annotationForm.selectedAnnotationForEdit!.id;
    const model: AddCommentDto = {
      parentIds,
      comment: {
        message,
        itemId
      }
    };

    try {
      const response = await axios.post(`comments/addcomment`, model);
      dispatch(getAnnotationComments(false));
      dispatch(setCommentsCount(commentsCount + 1));
      dispatch(setAddedResponseFlags({ addedFailed: false, addedSuccessfully: true }));
    } catch (error) {
      dispatch(setAddedResponseFlags({ addedFailed: true, addedSuccessfully: false }));
    }
  };

export const editComment =
  (parentIds: string[], id: string, message: string): AppThunk =>
  async dispatch => {
    const model: UpdateCommentDto = {
      parentIds,
      id,
      message
    };

    try {
      const response = await axios.post(`comments/editcomment`, model);
      dispatch(getAnnotationComments(false));
      dispatch(setAddedResponseFlags({ addedFailed: false, addedSuccessfully: true }));
    } catch (error) {
      dispatch(setAddedResponseFlags({ addedFailed: true, addedSuccessfully: false }));
    }
  };

export const deleteComment =
  (parentIds: string[], id: string): AppThunk =>
  async (dispatch, getState) => {
    const commentsCount = getState().annotationComments.commentsCount;
    const model: DeleteCommentDto = {
      parentIds,
      id
    };
    const response = await axios.post(`comments/deletecomment`, model);
    dispatch(getAnnotationComments(false));
    dispatch(setCommentsCount(commentsCount - 1));
  };
